import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { fetchWithAuth } from '../util/fetchWithAuth';
import { CustomerCreditBalance } from '../types';
import { formatCurrency } from '../util/formatCurrency';

type Props = {
  page: number;
  onPageChange: (page: number) => void;
};

const CustomerCreditBalances: React.FC<Props> = ({ page, onPageChange }) => {
  const [customers, setCustomers] = useState<CustomerCreditBalance[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [totalCredit, setTotalCredit] = useState('0');
  const [customerCount, setCustomerCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchTerm), 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    const fetchBalances = async () => {
      setIsLoading(true);
      try {
        const query = new URLSearchParams();
        if (debouncedSearch) query.append('customer_search', debouncedSearch);
        query.append('page', String(page));
        query.append('per_page', '10');

        const res = await fetchWithAuth(`/credit_transactions/balances?${query.toString()}`);

        if (!res.ok) {
          throw new Error('Failed to fetch customer credit balances');
        }

        const data = await res.json();
        setCustomers(data.customers);
        setTotalCredit(data.summary.total_credit);
        setCustomerCount(data.summary.customer_count);
        setTotalPages(data.pagination.total_pages);
      } catch (error) {
        console.error('Error fetching customer credit balances:', error);
        setCustomers([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchBalances();
  }, [debouncedSearch, page]);

  const formatDateTime = (dateString: string | null | undefined) => {
    if (!dateString) return '—';
    const date = new Date(dateString);
    return isNaN(date.getTime()) ? '—' : date.toLocaleDateString();
  };

  return (
    <>
      <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 mb-6">
        <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
          <div className="w-full md:w-72">
            <label htmlFor="balanceSearch" className="block text-sm font-medium text-gray-700 mb-1">
              Customer Search
            </label>
            <input
              type="text"
              id="balanceSearch"
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                onPageChange(1);
              }}
              placeholder="Name or customer code..."
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div className="md:text-right">
            <div className="text-2xl font-bold text-gray-900">
              {formatCurrency(parseFloat(totalCredit))}
            </div>
            <p className="text-sm text-gray-600">
              held across {customerCount} customer{customerCount !== 1 ? 's' : ''}
            </p>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200 bg-gray-50">
                <th className="px-2 md:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Customer
                </th>
                <th className="px-2 md:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Credit Balance
                </th>
                <th className="px-2 md:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Last Activity
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {isLoading ? (
                <tr>
                  <td colSpan={3} className="px-6 py-12 text-center text-gray-500">
                    Loading customer balances...
                  </td>
                </tr>
              ) : customers.length === 0 ? (
                <tr>
                  <td colSpan={3} className="px-6 py-12 text-center text-gray-500">
                    No customers currently hold credit
                  </td>
                </tr>
              ) : (
                customers.map((customer) => (
                  <tr key={customer.id} className="hover:bg-gray-50">
                    <td className="px-2 md:px-6 py-4">
                      <Link to={`/customers/${customer.id}`} className="hover:underline">
                        <div className="text-sm font-medium text-gray-900">{customer.customer_name}</div>
                        <div className="text-sm text-gray-500">{customer.customer_code}</div>
                      </Link>
                    </td>
                    <td className="px-2 md:px-6 py-4 text-sm font-medium text-gray-900 whitespace-nowrap">
                      {formatCurrency(parseFloat(customer.credit_balance))}
                    </td>
                    <td className="px-2 md:px-6 py-4 text-sm text-gray-500 whitespace-nowrap">
                      {formatDateTime(customer.last_activity_at)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {totalPages > 1 && (
          <div className="px-6 py-3 flex items-center justify-between border-t border-gray-200">
            <div className="flex-1 flex justify-between sm:hidden">
              <button
                onClick={() => onPageChange(page - 1)}
                disabled={page <= 1}
                className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
              >
                Previous
              </button>
              <button
                onClick={() => onPageChange(page + 1)}
                disabled={page >= totalPages}
                className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
              >
                Next
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700">
                  Showing page <span className="font-medium">{page}</span> of{' '}
                  <span className="font-medium">{totalPages}</span>
                </p>
              </div>
              <div>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                  <button
                    onClick={() => onPageChange(page - 1)}
                    disabled={page <= 1}
                    className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => onPageChange(page + 1)}
                    disabled={page >= totalPages}
                    className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                  >
                    Next
                  </button>
                </nav>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default CustomerCreditBalances;
